import React, { useState } from 'react';
import {
  ArrowLeft,
  MoreHorizontal,
  MessageCircle,
  Repeat2,
  Heart,
  Bookmark,
  Upload,
} from 'lucide-react';

const GRAD = 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)';
const GRAD_BTN = 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)';
const GRAD_BTN_HOVER = 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)';
const BORDER_COLOR = '#e0e7ff';
const BG_PAGE = '#f5f3ff';
const TEXT_MAIN = '#1e1b4b';
const TEXT_MUTED = '#6b7280';

/* ─── MemefyLogo ────────────────────────────────────────────────────── */

function MemefyLogo() {
  return (
    <div className="flex items-center pt-4 px-4 pb-2">
      <img src="/memefy-logo.png" alt="Memefy" style={{ height: '44px', width: 'auto' }} />
    </div>
  );
}

/* ─── VerifiedBadge ─────────────────────────────────────────────────── */

function VerifiedBadge() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline', flexShrink: 0 }}>
      <defs>
        <linearGradient id="vbGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="12" fill="url(#vbGrad)" />
      <path d="M9.5 16.5L5.5 12.5L6.91 11.09L9.5 13.67L17.09 6.08L18.5 7.5L9.5 16.5Z" fill="white" />
    </svg>
  );
}

/* ─── ProBadge ──────────────────────────────────────────────────────── */

function ProBadge() {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 6px',
      height: '18px',
      borderRadius: '9999px',
      background: GRAD_BTN,
      color: '#fff',
      fontWeight: 800,
      fontSize: '10px',
      letterSpacing: '0.5px',
      flexShrink: 0,
    }}>
      PRO
    </span>
  );
}

/* ─── ProfileAvatar ─────────────────────────────────────────────────── */

function ProfileAvatar() {
  return (
    <div style={{
      width: '46px',
      height: '46px',
      borderRadius: '50%',
      overflow: 'hidden',
      flexShrink: 0,
      border: '2px solid transparent',
      backgroundImage: `${GRAD}, white`,
      backgroundOrigin: 'border-box',
      backgroundClip: 'border-box',
      padding: '2px',
      position: 'relative',
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #fde68a 0%, #f97316 60%, #ef4444 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '22px',
        overflow: 'hidden',
      }}>
        🕵️
      </div>
    </div>
  );
}

/* ─── Header ────────────────────────────────────────────────────────── */

function Header() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 16px',
      borderBottom: `1px solid ${BORDER_COLOR}`,
      position: 'sticky',
      top: 0,
      background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(8px)',
      zIndex: 10,
      minHeight: '53px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '6px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TEXT_MAIN,
        }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#ede9fe')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
        >
          <ArrowLeft size={19} strokeWidth={2.2} />
        </button>
        <span style={{ fontWeight: 700, fontSize: '19px', color: TEXT_MAIN, lineHeight: 1.2 }}>
          Meme Details
        </span>
      </div>
      <button style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '6px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: TEXT_MUTED,
      }}
        onMouseEnter={(e) => (e.currentTarget.style.background = '#ede9fe')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
      >
        <MoreHorizontal size={19} strokeWidth={2} />
      </button>
    </div>
  );
}

/* ─── ActionBar ─────────────────────────────────────────────────────── */

function ActionBar() {
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const buttons = [
    { icon: <MessageCircle size={20} strokeWidth={1.8} />, label: 'Reply', color: '#4f46e5' },
    { icon: <Repeat2 size={20} strokeWidth={1.8} />, label: 'ReMeme', color: '#10b981' },
    {
      icon: <Heart size={20} strokeWidth={1.8} fill={liked ? '#ec4899' : 'none'} />,
      label: 'Like',
      color: '#ec4899',
      onClick: () => setLiked(!liked),
      active: liked,
    },
    {
      icon: <Bookmark size={20} strokeWidth={1.8} fill={bookmarked ? '#4f46e5' : 'none'} />,
      label: 'Bookmark',
      color: '#4f46e5',
      onClick: () => setBookmarked(!bookmarked),
      active: bookmarked,
    },
    { icon: <Upload size={20} strokeWidth={1.8} />, label: 'Share', color: '#7c3aed' },
  ];

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      paddingTop: '4px',
      paddingBottom: '2px',
    }}>
      {buttons.map(({ icon, label, color, onClick, active }) => (
        <button
          key={label}
          aria-label={label}
          onClick={onClick}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '9px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: active ? color : TEXT_MUTED,
            transition: 'color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ede9fe';
            e.currentTarget.style.color = color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.color = active ? color : TEXT_MUTED;
          }}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

/* ─── PostCard ──────────────────────────────────────────────────────── */

function PostCard() {
  return (
    <div style={{ padding: '12px 16px 0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <ProfileAvatar />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '15px', color: TEXT_MAIN, lineHeight: 1.4 }}>
              The Tan Investigator
            </span>
            <VerifiedBadge />
            <ProBadge />
          </div>
          <div style={{ fontSize: '14px', color: TEXT_MUTED, lineHeight: 1.3, marginTop: '1px' }}>
            @TanPatrol2026
          </div>
        </div>
      </div>

      <div style={{
        marginTop: '14px',
        fontSize: '15px',
        lineHeight: '1.7',
        color: TEXT_MAIN,
        wordBreak: 'break-word',
      }}>
        Breaking: Scientists have confirmed that the orange glow emanating from Mar-a-Lago
        is NOT the sun. It is, in fact, a single man's face. 🟠
        <br /><br />
        Sources say dermatologists have classified Trump's tan as a "new Pantone color" —
        officially named <strong>Dorito Dust #45</strong>. The FDA is currently investigating
        whether the tan is contagious through handshakes.
        <br /><br />
        Meteorologists in Florida have begun factoring his cheek reflection into UV index
        forecasts. Lifeguards are reportedly issuing SPF 100 warnings whenever he steps
        onto the golf course. ☀️🏌️
        <br /><br />
        In a press briefing, one reporter asked: "Sir, where does the tan end and the suit begin?"
        He was immediately escorted out of the building. 😭
        <br /><br />
        This has been your <strong>Tan Patrol 2026</strong> update. Stay shaded out there. 🧴
      </div>

      <div style={{
        marginTop: '14px',
        fontSize: '14px',
        color: TEXT_MUTED,
        paddingBottom: '12px',
      }}>
        May 13, 2026, 7:50 PM · Dubai, UAE
      </div>

      <div style={{
        display: 'flex',
        gap: '20px',
        padding: '12px 0',
        borderTop: `1px solid ${BORDER_COLOR}`,
        borderBottom: `1px solid ${BORDER_COLOR}`,
        fontSize: '14px',
        color: TEXT_MAIN,
      }}>
        <span>
          <strong style={{ fontWeight: 700 }}>2.48k</strong>
          <span style={{ color: TEXT_MUTED, marginLeft: '4px' }}>ReMemes</span>
        </span>
        <span>
          <strong style={{ fontWeight: 700 }}>9.8k</strong>
          <span style={{ color: TEXT_MUTED, marginLeft: '4px' }}>Likes</span>
        </span>
        <span>
          <strong style={{ fontWeight: 700 }}>4.1k</strong>
          <span style={{ color: TEXT_MUTED, marginLeft: '4px' }}>Replies</span>
        </span>
      </div>

      <div style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
        <ActionBar />
      </div>
    </div>
  );
}

/* ─── Sidebar ───────────────────────────────────────────────────────── */

function GradientButton({ children, style = {}, ...props }) {
  return (
    <button
      style={{
        display: 'block',
        width: '100%',
        padding: '10px 0',
        background: GRAD_BTN,
        color: '#fff',
        borderRadius: '9999px',
        textAlign: 'center',
        fontWeight: 700,
        fontSize: '15px',
        textDecoration: 'none',
        border: 'none',
        cursor: 'pointer',
        ...style,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = GRAD_BTN_HOVER)}
      onMouseLeave={(e) => (e.currentTarget.style.background = GRAD_BTN)}
      {...props}
    >
      {children}
    </button>
  );
}

function OutlineButton({ children, style = {}, ...props }) {
  return (
    <button
      style={{
        display: 'block',
        width: '100%',
        padding: '10px 0',
        background: '#ffffff',
        color: TEXT_MAIN,
        borderRadius: '9999px',
        textAlign: 'center',
        fontWeight: 700,
        fontSize: '15px',
        border: `1.5px solid ${BORDER_COLOR}`,
        cursor: 'pointer',
        ...style,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f3ff')}
      onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
      {...props}
    >
      {children}
    </button>
  );
}

function Sidebar() {
  return (
    <div style={{ width: '350px', flexShrink: 0, padding: '12px 0 12px 28px' }}>

      {/* Trending Memes card */}
      <div style={{
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '16px',
        background: '#ffffff',
        boxShadow: '0 2px 12px rgba(79,70,229,0.06)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}>
          <span style={{
            display: 'inline-block',
            width: '6px',
            height: '28px',
            borderRadius: '3px',
            background: GRAD,
          }} />
          <h3 style={{ fontWeight: 800, fontSize: '18px', color: TEXT_MAIN, margin: 0 }}>
            Trending Memes
          </h3>
        </div>
        {['#TanGate2026', '#DoritoDust45', '#GolfCourseGlow', '#SPF100', '#MemeOfTheDay'].map((tag, i) => (
          <div key={tag} style={{
            padding: '8px 0',
            borderBottom: i < 4 ? `1px solid ${BORDER_COLOR}` : 'none',
            fontSize: '14px',
            cursor: 'pointer',
          }}>
            <div style={{ fontWeight: 700, color: TEXT_MAIN }}>{tag}</div>
            <div style={{ color: TEXT_MUTED, fontSize: '13px' }}>{(12 - i * 2).toFixed(1)}k posts</div>
          </div>
        ))}
      </div>

      {/* Join card */}
      <div style={{
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '16px',
        background: '#ffffff',
        boxShadow: '0 2px 12px rgba(79,70,229,0.06)',
      }}>
        <h3 style={{ fontWeight: 800, fontSize: '19px', color: TEXT_MAIN, margin: '0 0 4px 0' }}>
          New to Memefy? 😂
        </h3>
        <p style={{ fontSize: '14px', color: TEXT_MUTED, margin: '0 0 16px 0', lineHeight: 1.5 }}>
          Join the internet's most unhinged meme feed. No cap, all laughs.
        </p>
        <GradientButton style={{ marginBottom: '10px' }}>Create Account</GradientButton>
        <OutlineButton>Sign In</OutlineButton>
      </div>

      {/* Memefy Pro card */}
      <div style={{
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '16px',
        background: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
        boxShadow: '0 2px 12px rgba(79,70,229,0.08)',
      }}>
        <h3 style={{ fontWeight: 800, fontSize: '19px', color: TEXT_MAIN, margin: '0 0 4px 0' }}>
          Get Memefy Pro ✨
        </h3>
        <p style={{ fontSize: '14px', color: TEXT_MUTED, margin: '0 0 16px 0', lineHeight: 1.5 }}>
          Unlimited meme uploads, custom reactions, and rainbow badges.
        </p>
        <GradientButton style={{ width: 'auto', display: 'inline-block', padding: '10px 24px' }}>
          Subscribe
        </GradientButton>
      </div>

      {/* Footer */}
      <div style={{ padding: '0 4px', fontSize: '13px', color: TEXT_MUTED }}>
        <a href="#" style={{ color: TEXT_MUTED, textDecoration: 'none' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#4f46e5')}
          onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_MUTED)}>
          Help
        </a>
        {' · '}
        <a href="#" style={{ color: TEXT_MUTED, textDecoration: 'none' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#4f46e5')}
          onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_MUTED)}>
          Privacy
        </a>
        {' · '}
        <a href="#" style={{ color: TEXT_MUTED, textDecoration: 'none' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#4f46e5')}
          onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_MUTED)}>
          Terms
        </a>
        <div style={{ marginTop: '6px' }}>© 2026 Memefy, Inc.</div>
      </div>
    </div>
  );
}

/* ─── FooterSection ─────────────────────────────────────────────────── */

function FooterSection() {
  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'rgba(255,255,255,0.97)',
      borderTop: `1px solid ${BORDER_COLOR}`,
      backdropFilter: 'blur(8px)',
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 20,
      boxShadow: '0 -4px 20px rgba(79,70,229,0.08)',
    }}>
      <div>
        <div style={{ fontWeight: 800, fontSize: '18px', color: TEXT_MAIN, lineHeight: 1.3 }}>
          Don't miss the memes 🎭
        </div>
        <div style={{ fontSize: '14px', color: TEXT_MUTED, marginTop: '2px' }}>
          Sign up to join the conversation and post your own.
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px', flexShrink: 0, marginLeft: '16px' }}>
        <button
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '10px 22px',
            background: GRAD_BTN,
            color: '#fff',
            borderRadius: '9999px',
            fontWeight: 700,
            fontSize: '15px',
            whiteSpace: 'nowrap',
            border: 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = GRAD_BTN_HOVER)}
          onMouseLeave={(e) => (e.currentTarget.style.background = GRAD_BTN)}
        >
          Create Account
        </button>
        <button
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '10px 22px',
            background: '#ffffff',
            color: TEXT_MAIN,
            borderRadius: '9999px',
            fontWeight: 700,
            fontSize: '15px',
            whiteSpace: 'nowrap',
            border: `1.5px solid ${BORDER_COLOR}`,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f3ff')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
        >
          Sign In
        </button>
      </div>
    </div>
  );
}

/* ─── App ───────────────────────────────────────────────────────────── */

export default function App() {
  return (
    <div style={{
      minHeight: '100vh',
      background: BG_PAGE,
      backgroundImage: 'radial-gradient(ellipse at top left, #ede9fe 0%, transparent 50%), radial-gradient(ellipse at bottom right, #fce7f3 0%, transparent 50%)',
      display: 'flex',
      justifyContent: 'center',
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{
        display: 'flex',
        width: '100%',
        maxWidth: '1265px',
        position: 'relative',
      }}>

        {/* ── Left nav ── */}
        <div style={{ width: '275px', flexShrink: 0, paddingRight: '8px' }}>
          <MemefyLogo />
        </div>

        {/* ── Center feed ── */}
        <div style={{
          flex: '1 1 auto',
          maxWidth: '600px',
          minWidth: 0,
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          paddingBottom: '140px',
          background: '#ffffff',
        }}>
          <Header />

          <div style={{
            padding: '8px 16px 4px 16px',
            fontSize: '14px',
            color: TEXT_MUTED,
          }}>
            4,481 replies
          </div>

          <PostCard />
        </div>

        {/* ── Right sidebar ── */}
        <Sidebar />
      </div>

      <FooterSection />
    </div>
  );
}
